// Domain model + pure helpers for admin Group Trial management (SEN-6115).
// The server contract lives in the admin-service GraphQL API (CreateGroupTrial /
// UpdateGroupTrial / UpdateGroupTrialStatus / AdminRetrieveGroupTrials).

export type GroupTrialStatus = 'ENROLLING' | 'IN_PROGRESS' | 'COMPLETED';

// A single per-participant assessment allowance.
export type QuotaEntry = { assessmentId: string; quota: number };

// A GroupTrial as returned by the admin read query / mutations. `created` is a
// _Neo4jDateTime selected as { formatted } (an ISO string), matching every other
// date field; `assessmentQuotas` is a serialized JSON string.
export interface GroupTrial {
  id: string;
  name: string;
  description?: string | null;
  status: GroupTrialStatus;
  maxParticipants: number;
  assessmentQuotas?: string | null; // JSON string -> { entries: QuotaEntry[] }
  created?: { formatted: string } | null;
}

// Assessment id -> display label. Only the clinic Brain Years assessment is a
// valid quota key today; the structure supports more ids later.
export const ASSESSMENT_LABELS: Record<string, string> = {
  'ASMT-CLINIC_BY': 'Brain Years'
};

// The only assessment id an admin can currently configure a quota for.
export const DEFAULT_ASSESSMENT_ID = 'ASMT-CLINIC_BY';

// Forward-only linear lifecycle order.
export const STATUS_ORDER: GroupTrialStatus[] = [
  'ENROLLING',
  'IN_PROGRESS',
  'COMPLETED'
];

const STATUS_LABELS: Record<GroupTrialStatus, string> = {
  ENROLLING: 'Enrolling',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed'
};

// Human-friendly label for a status.
export const statusLabel = (status: GroupTrialStatus): string =>
  STATUS_LABELS[status] ?? status;

// The next forward status in the lifecycle, or null when at the terminal state.
export const nextGroupTrialStatus = (
  status: GroupTrialStatus
): GroupTrialStatus | null => {
  const i = STATUS_ORDER.indexOf(status);
  if (i < 0 || i >= STATUS_ORDER.length - 1) {
    return null;
  }
  return STATUS_ORDER[i + 1];
};

// Label for the action that advances a trial to its next lifecycle state,
// phrased as the concrete transition it performs. COMPLETED is terminal and has
// no advance action, so it falls back to the generic verb.
export const advanceActionLabel = (status: GroupTrialStatus): string => {
  switch (status) {
    case 'ENROLLING':
      return 'Start trial';
    case 'IN_PROGRESS':
      return 'End trial';
    default:
      return 'Advance';
  }
};

// Config edits (name/description/cap/quotas) are locked once COMPLETED.
export const canEditGroupTrial = (status: GroupTrialStatus): boolean =>
  status !== 'COMPLETED';

// Parse the serialized `assessmentQuotas` JSON string into quota rows.
// Authoritative shape: { entries: [{ assessmentId, quota }] }. Any malformed or
// unexpected payload degrades gracefully to an empty list.
export const parseAssessmentQuotas = (json?: string | null): QuotaEntry[] => {
  if (!json) {
    return [];
  }
  try {
    const parsed = JSON.parse(json);
    const entries = parsed?.entries;
    if (!Array.isArray(entries)) {
      return [];
    }
    return entries
      .filter(
        (e) =>
          e &&
          typeof e.assessmentId === 'string' &&
          typeof e.quota === 'number'
      )
      .map((e) => ({ assessmentId: e.assessmentId, quota: e.quota }));
  } catch {
    return [];
  }
};

// A quota row as edited in the form (quota kept as a string for the input).
export interface QuotaRow {
  assessmentId: string;
  quota: string;
}

// Whether a string parses to a whole number >= 1.
const isPositiveInt = (value: string): boolean => {
  if (value.trim() === '') {
    return false;
  }
  const n = Number(value);
  return Number.isInteger(n) && n >= 1;
};

// Normalize form rows into mutation entries: drop blank rows, coerce the rest to
// integers. The result is the bare `quotas` list the mutations expect.
export const normalizeQuotaEntries = (rows: QuotaRow[]): QuotaEntry[] =>
  rows
    .filter((row) => row.quota.trim() !== '')
    .map((row) => ({ assessmentId: row.assessmentId, quota: Number(row.quota) }));

// The editable form value shape.
export interface GroupTrialFormValues {
  name: string;
  description: string;
  maxParticipants: string;
  quotas: QuotaRow[];
}

// Validate the form. Returns an error message, or undefined when valid.
export const validateGroupTrialForm = (
  values: GroupTrialFormValues
): string | undefined => {
  if (!values.name.trim()) {
    return 'Trial name is required.';
  }
  if (!isPositiveInt(values.maxParticipants)) {
    return 'Max participants must be a whole number of at least 1.';
  }
  for (const row of values.quotas) {
    if (row.quota.trim() === '') {
      continue; // blank quota => no allowance for this assessment (allowed)
    }
    if (!isPositiveInt(row.quota)) {
      const label = ASSESSMENT_LABELS[row.assessmentId] ?? row.assessmentId;
      return `${label} quota must be a whole number of at least 1.`;
    }
  }
  return undefined;
};

// A short, human-readable summary of a trial's per-participant quotas, e.g.
// "Brain Years: 3". Returns "—" when there are none.
export const quotaSummary = (assessmentQuotas?: string | null): string => {
  const entries = parseAssessmentQuotas(assessmentQuotas);
  if (entries.length === 0) {
    return '—';
  }
  return entries
    .map(
      (e) => `${ASSESSMENT_LABELS[e.assessmentId] ?? e.assessmentId}: ${e.quota}`
    )
    .join(', ');
};
